import {
  ORDER_STOP_DONE_STATUSES,
  ORDER_STOP_STATUSES,
} from 'utils/orderStop/constants';
import { isStatusBeforeAssigned } from 'utils/order/methods';
import { ORDER_STATUSES } from 'utils/order/constants';

const mapStopWithStateProperties = ({ stop }) => {
  const { summary } = stop || {};
  const { on_route, in_progress, done } = summary || {};

  return {
    ...stop,
    is_on_way: !!on_route && !in_progress && !done,
    is_loading_or_unloading: !!in_progress && !done,
    is_done: ORDER_STOP_DONE_STATUSES.includes(stop.status),
    is_in_progress: stop.status === ORDER_STOP_STATUSES.IN_PROGRESS,
    is_pending: stop.status === ORDER_STOP_STATUSES.PENDING,

    arrival_time: in_progress,
    load_or_unload_time: done,
  };
};

const getStopLabelValue = ({ stop: currentStop }) => {
  if (!currentStop) {
    return {};
  }

  const stopType = currentStop.stop_type?.toLowerCase();

  if (currentStop.is_done) {
    return { value: `statuses.${stopType}.done` };
  }

  if (!currentStop.store && currentStop.possible_standstill) {
    return {
      value: `statuses.${stopType}.possible_standstill`,
      style: 'warning',
      hasStandstillOrPossibility: true,
    };
  }

  if (!currentStop.store && currentStop.standstill) {
    return {
      value: `statuses.${stopType}.standstill`,
      style: 'warning',
      hasStandstillOrPossibility: true,
    };
  }

  if (currentStop.is_on_way) {
    return { value: `statuses.${stopType}.on_way` };
  }

  if (currentStop.is_loading_or_unloading) {
    return { value: `statuses.${stopType}.on_progress` };
  }

  return { value: `${stopType}` };
};

const findLast = (arr) => (fn) => [...(arr || [])].reverse().find(fn);

const OrderStatusesObject = {
  nonCorePending: () => ({
    subTitle: {
      value: 'non_core.pending_confirmation',
      icon: 'girded-exclamation',
      style: 'warning',
    },
  }),
  prepare: () => ({
    subTitle: {
      value: 'wait',
      icon: false,
      style: 'prepare',
    },
  }),
  driverPrepare: () => ({
    subTitle: {
      value: 'driver.prepare',
      icon: 'success',
      style: 'chill',
    },
  }),
  completed: () => ({
    subTitle: {
      value: 'completed',
      icon: 'success',
      style: 'chill',
    },
  }),
  cancelled: () => ({
    subTitle: {
      value: 'cancel',
      icon: false,
      style: 'error',
    },
  }),
  assigned: ({ order }) => {
    const [firstDriver] = order?.drivers || [];
    const { contact_name: driverName } = firstDriver || {};

    return {
      subTitle: {
        value: 'driver.assigned',
        icon: 'status-driver',
        style: driverName ? 'assigned' : 'prepare',
        driverName,
      },
    };
  },
  inTransit: ({ order }) => {
    const stops = order?.stops?.map((stop) =>
      mapStopWithStateProperties({
        stop,
      })
    );

    const currentStop =
      findLast(stops)((stop) => stop.is_in_progress) ||
      findLast(stops)((stop) => stop.is_done);

    const { value, style } = getStopLabelValue({ stop: currentStop });

    return {
      subTitle: {
        icon: false,
        style: style || 'chill',
        value: value,
        address: currentStop?.address,
      },
      isCrossDock: !!currentStop?.store,
    };
  },
};

const orderStatuses = {
  [ORDER_STATUSES.PENDING_PAYMENT]: OrderStatusesObject.prepare,
  [ORDER_STATUSES.PENDING_CONFIRMATION]: OrderStatusesObject.prepare,
  [ORDER_STATUSES.PENDING_MODERATION]: OrderStatusesObject.driverPrepare,
  [ORDER_STATUSES.PENDING_ASSIGNMENT]: OrderStatusesObject.driverPrepare,
  [ORDER_STATUSES.ASSIGNED]: OrderStatusesObject.assigned,
  [ORDER_STATUSES.IN_TRANSIT]: OrderStatusesObject.inTransit,
  [ORDER_STATUSES.DONE_WITHOUT_MANIFEST]: OrderStatusesObject.completed,
  [ORDER_STATUSES.DONE_WITH_CANCELLATIONS]: OrderStatusesObject.completed,
  [ORDER_STATUSES.DONE]: OrderStatusesObject.completed,
  [ORDER_STATUSES.CANCELLED]: OrderStatusesObject.cancelled,
  [ORDER_STATUSES.NON_CORE_PENDING_CONFIRMATION]:
    OrderStatusesObject.nonCorePending,
};

const getStatusConfig = (order, isOrderNonCore = false) => {
  if (isOrderNonCore && isStatusBeforeAssigned(order?.status)) {
    return orderStatuses.NON_CORE_PENDING_CONFIRMATION({ order });
  }
  if (!orderStatuses[order?.status]) {
    return false;
  }
  return orderStatuses[order.status]({ order });
};

export { orderStatuses };
export default getStatusConfig;
